/**
 * Ranking UI - Displays player stats and leaderboard
 */

import { isMobileDevice } from '../utils/device.js';

export class RankingUI {
    constructor(game) {
        this.game = game;
        this.isVisible = false;
        this.isMobileMode = isMobileDevice();
        this.handleResize = this.handleResize.bind(this);
        window.addEventListener('resize', this.handleResize);
        
        // Create ranking panel
        this.createRankingPanel();
    }
    
    createRankingPanel() {
        // Create ranking panel element - Figma design
        this.rankingPanel = document.createElement('div');
        this.rankingPanel.id = 'rankingPanel';
        const topPosition = this.isMobileMode ? '5px' : '20px';
        this.rankingPanel.style.cssText = `
            position: absolute;
            top: ${topPosition};
            left: 20px;
            width: 127px;
            background: transparent;
            border: none;
            border-radius: 0;
            padding: 0;
            z-index: 150;
            display: none;
            font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif;
        `;
        
        // Create panel content - Figma design with pirate icons
        this.rankingPanel.innerHTML = `
            <div style="
                padding: 8px 16px;
                margin-bottom: 8px;
                display: flex;
                align-items: center;
                gap: 6px;
            ">
                <img src="./assets/images/ui/haidaoqi.png" style="
                    width: 24px;
                    height: 24px;
                    object-fit: contain;
                " alt="海盗旗">
                <h3 style="
                    margin: 0;
                    color: #FFFFFF;
                    font-family: 'SF Pro Display', sans-serif;
                    font-size: 20px;
                    font-weight: 700;
                    line-height: 1.3;
                    letter-spacing: -0.04em;
                    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.8);
                ">Pirates</h3>
            </div>
            <div id="leaderboardList" style="
                display: flex;
                flex-direction: column;
                gap: 2px;
            "></div>
        `;
        
        // Add to game container
        document.getElementById('gameContainer').appendChild(this.rankingPanel);
    }
    
    show() {
        this.isMobileMode = isMobileDevice();

        if (this.rankingPanel) {
            this.rankingPanel.style.display = 'block';
            this.isVisible = true;
        }
    }
    
    handleResize() {
        const isMobile = isMobileDevice();
        if (isMobile !== this.isMobileMode) {
            this.isMobileMode = isMobile;
            // Update top position based on device type
            const topPosition = this.isMobileMode ? '5px' : '20px';
            if (this.rankingPanel) {
                this.rankingPanel.style.top = topPosition;
            }
            // Show ranking panel on both desktop and mobile
            if (this.game && this.game.isRunning) {
                this.show();
            }
        }
    }

    hide() {
        if (this.rankingPanel) {
            this.rankingPanel.style.display = 'none';
            this.isVisible = false;
        }
    }
    
    update(deltaTime) {
        // Update is called from the game loop, we need to get the ranking system from the game
        if (!this.isVisible || !this.game || !this.game.rankingSystem) return;

        // Get all ships ranked by gold coins
        const allShipsRanking = this.game.rankingSystem.getAllShipsByGold();

        // Update leaderboard with Figma design
        const leaderboardList = document.getElementById('leaderboardList');
        if (leaderboardList && allShipsRanking) {
            let html = '';

            // Only show top 5
            const top5 = allShipsRanking.slice(0, 5);

            top5.forEach((entry, index) => {
                const isPlayer = entry.isPlayer;
                const bgColor = isPlayer ? 'rgba(236, 191, 3, 0.3)' : 'rgba(0, 0, 0, 0.3)';

                html += `
                    <div style="
                        width: 111px;
                        height: 16px;
                        background: ${bgColor};
                        border-radius: 1px;
                        display: flex;
                        align-items: center;
                        padding: 0 6px;
                        box-sizing: border-box;
                    ">
                        <span style="
                            font-family: 'Helvetica', sans-serif;
                            font-size: 12px;
                            font-weight: 700;
                            color: #FFFFFF;
                            flex-shrink: 0;
                        ">${index + 1}</span>
                        <span style="
                            font-family: 'YouSheBiaoTiYuan', -apple-system, BlinkMacSystemFont, sans-serif;
                            font-size: 12px;
                            font-weight: 400;
                            color: #FFFFFF;
                            flex: 1;
                            text-align: center;
                            margin: 0 4px;
                            overflow: hidden;
                            text-overflow: ellipsis;
                            white-space: nowrap;
                        ">${entry.name}</span>
                        <span style="
                            font-family: 'YouSheBiaoTiYuan', -apple-system, BlinkMacSystemFont, sans-serif;
                            font-size: 10px;
                            font-weight: 400;
                            color: #EDCC4C;
                            flex-shrink: 0;
                        ">${entry.gold}</span>
                    </div>
                `;
            });

            leaderboardList.innerHTML = html;
        }
    }
    
    // Clean up
    destroy() {
        if (this.rankingPanel && this.rankingPanel.parentNode) {
            this.rankingPanel.parentNode.removeChild(this.rankingPanel);
        }
        window.removeEventListener('resize', this.handleResize);
    }

    /**
     * Show kill notification when an enemy is destroyed
     * @param {string} enemyName - Name of the destroyed enemy
     */
    showKillNotification(enemyName) {
        // 清除所有旧提醒
        this.clearAllNotifications();

        // Create notification element
        const notification = document.createElement('div');
        notification.className = 'game-notification'; // 添加标识class
        notification.style.cssText = `
            position: fixed;
            top: 15%;
            left: 50%;
            transform: translate(-50%, -50%);
            background: rgba(255, 50, 50, 0.9);
            color: white;
            padding: 8px 16px;
            border-radius: 6px;
            font-size: 14px;
            font-weight: bold;
            z-index: 1000;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
            animation: fadeInOut 2s ease-in-out;
        `;

        notification.textContent = `击杀了 ${enemyName}!`;

        // Add animation style if not exists
        if (!document.getElementById('killNotificationStyle')) {
            const style = document.createElement('style');
            style.id = 'killNotificationStyle';
            style.textContent = `
                @keyframes fadeInOut {
                    0% { opacity: 0; transform: translate(-50%, -70px); }
                    20% { opacity: 1; transform: translate(-50%, -50%); }
                    80% { opacity: 1; transform: translate(-50%, -50%); }
                    100% { opacity: 0; transform: translate(-50%, -70px); }
                }
            `;
            document.head.appendChild(style);
        }

        // Add to document
        document.body.appendChild(notification);

        // Remove after animation completes
        setTimeout(() => {
            if (notification.parentNode) {
                notification.parentNode.removeChild(notification);
            }
        }, 2000);
    }

    /**
     * Update coin display in the UI (deprecated - now using game status bar)
     * @param {number} coinCount - Current number of coins
     */
    updateCoinDisplay(coinCount) {
        // Coin display moved to game status bar
        // This method is kept for backward compatibility
    }

    /**
     * 清除所有游戏提醒
     */
    clearAllNotifications() {
        const notifications = document.querySelectorAll('.game-notification');
        notifications.forEach(notification => {
            if (notification.parentNode) {
                notification.parentNode.removeChild(notification);
            }
        });
    }

    /**
     * Show death notification when player dies
     */
    showDeathNotification() {
        // 清除所有旧提醒
        this.clearAllNotifications();

        // Create notification element
        const notification = document.createElement('div');
        notification.className = 'game-notification';
        notification.style.cssText = `
            position: fixed;
            top: 15%;
            left: 50%;
            transform: translate(-50%, -50%);
            background: rgba(150, 150, 150, 0.9);
            color: white;
            padding: 8px 16px;
            border-radius: 6px;
            font-size: 14px;
            font-weight: bold;
            z-index: 1000;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
            animation: fadeInOut 2s ease-in-out;
        `;

        notification.textContent = `💀 你被击沉了！准备复活...`;

        // Add animation style if not exists
        if (!document.getElementById('killNotificationStyle')) {
            const style = document.createElement('style');
            style.id = 'killNotificationStyle';
            style.textContent = `
                @keyframes fadeInOut {
                    0% { opacity: 0; transform: translate(-50%, -70px); }
                    20% { opacity: 1; transform: translate(-50%, -50%); }
                    80% { opacity: 1; transform: translate(-50%, -50%); }
                    100% { opacity: 0; transform: translate(-50%, -70px); }
                }
            `;
            document.head.appendChild(style);
        }

        // Add to document
        document.body.appendChild(notification);

        // Remove after animation completes
        setTimeout(() => {
            if (notification.parentNode) {
                notification.parentNode.removeChild(notification);
            }
        }, 2000);
    }
}